<template>
  <div>
    <Navigation />
    <div class="tabs-root">
      <header class="page-head">
        <div>
          <h2>Open tabs</h2>
          <p class="muted small">
            Running totals for walk-in bar tabs, salon chairs, and open tables.
            Add items over time — close when the customer pays.
          </p>
        </div>
        <button class="primary" @click="showNewTab = true">
          <i class="mdi mdi-plus-circle"></i> New walk-in tab
        </button>
      </header>

      <div class="filter-row card">
        <label>Filter</label>
        <div class="chips">
          <button :class="{ chip: true, active: filter === '' }" @click="filter = ''">All</button>
          <button :class="{ chip: true, active: filter === 'walkin' }" @click="filter = 'walkin'">Walk-in</button>
          <button :class="{ chip: true, active: filter === 'table' }" @click="filter = 'table'">Table</button>
          <button :class="{ chip: true, active: filter === 'takeaway' }" @click="filter = 'takeaway'">Takeaway</button>
        </div>
        <button class="ghost" @click="load" :disabled="loading">
          <i class="mdi mdi-refresh"></i> {{ loading ? 'Loading…' : 'Refresh' }}
        </button>
      </div>

      <div v-if="loading && !tabs.length" class="muted small" style="padding: 40px; text-align: center;">Loading tabs…</div>

      <div v-else-if="filteredTabs.length" class="tabs-grid">
        <article v-for="t in filteredTabs" :key="t.id" class="tab-card card" @click="openTab(t.id)">
          <header>
            <span :class="'type-pill ' + t.tab_type">{{ t.tab_type }}</span>
            <span class="opened" :class="{ aging: t.minutes_open >= 90 }">
              <i class="mdi mdi-clock-outline"></i>
              {{ friendlyMinutes(t.minutes_open) }}
            </span>
          </header>
          <h3>{{ displayName(t) }}</h3>
          <p class="meta muted small">
            <span v-if="t.waiter_name">{{ t.waiter_name }}</span>
            <span v-if="t.customer_name"> · {{ t.customer_name }}</span>
            <span v-if="t.guest_count > 1"> · {{ t.guest_count }} guests</span>
          </p>
          <footer>
            <span class="lines">{{ t.line_count }} line{{ Number(t.line_count) === 1 ? '' : 's' }}</span>
            <span class="total">KES {{ format(t.subtotal) }}</span>
          </footer>
        </article>
      </div>

      <div v-else class="muted small empty-state card">
        <i class="mdi mdi-glass-cocktail" style="font-size: 40px; opacity: .5;"></i>
        <p>No open tabs. Start one with the button above, or open a table from the Tables page.</p>
      </div>
    </div>

    <!-- New walk-in modal -->
    <div v-if="showNewTab" class="modal-back" @click.self="showNewTab = false">
      <div class="modal">
        <h3>New walk-in tab</h3>
        <p class="muted small">Give the tab a name — a person, a chair, or anything the till staff will recognise.</p>
        <label>Tab name</label>
        <input ref="tabName" v-model="newTabName" placeholder="e.g. Alex, Chair 2, Corner sofa" @keyup.enter="createTab" />
        <div class="modal-actions">
          <button class="ghost" @click="showNewTab = false">Cancel</button>
          <button class="primary" @click="createTab" :disabled="!newTabName.trim() || creating">
            {{ creating ? 'Opening…' : 'Open tab' }}
          </button>
        </div>
        <p v-if="err" class="err">{{ err }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { paysol, execute } from '@/api';
import Navigation from '@/components/paysol-nav/Navigation.vue';

export default {
  name: 'TabsPage',
  components: { Navigation },
  data() {
    return {
      loading: false, tabs: [], filter: '',
      showNewTab: false, newTabName: '', creating: false, err: '',
      pollTimer: null,
    };
  },
  computed: {
    filteredTabs() {
      if (!this.filter) return this.tabs;
      return this.tabs.filter(t => t.tab_type === this.filter);
    },
  },
  async mounted() {
    await this.load();
    // Poll every 30s so aging tabs surface without a manual refresh
    this.pollTimer = setInterval(() => this.load(true), 30000);
  },
  beforeUnmount() {
    if (this.pollTimer) clearInterval(this.pollTimer);
  },
  watch: {
    showNewTab(v) { if (v) this.$nextTick(() => this.$refs.tabName?.focus()); },
  },
  methods: {
    async load(silent = false) {
      if (!silent) this.loading = true;
      try {
        const { data } = await execute({ function: 'listOpenTabs' }, paysol);
        if (data.success) this.tabs = data.data || [];
      } finally { this.loading = false; }
    },
    async createTab() {
      const name = this.newTabName.trim();
      if (!name) return;
      this.err = ''; this.creating = true;
      try {
        const { data } = await execute({
          function: 'openTab', tabType: 'walkin', tabName: name,
        }, paysol);
        if (!data.success) { this.err = data.message; return; }
        this.showNewTab = false; this.newTabName = '';
        this.$router.push(`/waiter/${data.data.id}`);
      } catch (e) {
        this.err = e.response?.data?.message || 'Could not open tab.';
      } finally { this.creating = false; }
    },
    openTab(id) { this.$router.push(`/waiter/${id}`); },
    displayName(t) {
      if (t.tab_name) return t.tab_name;
      if (t.tab_type === 'table' && t.table_name) return t.table_name;
      if (t.tab_type === 'takeaway') return 'Takeaway';
      return `Tab #${t.id}`;
    },
    format(n) { return Number(n || 0).toLocaleString('en-KE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }); },
    friendlyMinutes(n) {
      const m = Math.max(0, Number(n) || 0);
      if (m < 60) return `${m}m open`;
      const h = Math.floor(m / 60);
      const r = m % 60;
      return r ? `${h}h ${r}m open` : `${h}h open`;
    },
  },
};
</script>

<style scoped>
.tabs-root { max-width: 1200px; margin: 0 auto; padding: 20px 20px 60px; }
.page-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; gap: 12px; flex-wrap: wrap; }
.page-head h2 { margin: 0 0 4px; }
.filter-row { display: flex; align-items: center; gap: 10px; padding: 12px 16px; margin-bottom: 16px; flex-wrap: wrap; }
.filter-row label { margin: 0; font-size: 12px; color: var(--paysol-muted); text-transform: uppercase; letter-spacing: 1px; }
.chips { display: flex; gap: 6px; }
.chip { background: transparent; border: 1px solid var(--paysol-line); color: var(--paysol-muted); padding: 6px 12px; border-radius: 20px; font-size: 12px; cursor: pointer; }
.chip.active { background: var(--paysol-teal); color: #fff; border-color: var(--paysol-teal); }
.filter-row .ghost { margin-left: auto; font-size: 12px; }

.tabs-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 12px; }
.tab-card { padding: 14px; cursor: pointer; transition: transform .1s, border-color .15s; }
.tab-card:hover { transform: translateY(-2px); border-color: var(--paysol-teal); }
.tab-card header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
.tab-card h3 { margin: 4px 0 2px; font-size: 16px; }
.tab-card .meta { margin: 0 0 12px; }
.tab-card footer { display: flex; justify-content: space-between; align-items: baseline; border-top: 1px dashed var(--paysol-line); padding-top: 10px; }
.tab-card .total { color: var(--paysol-teal); font-weight: 700; font-size: 18px; }
.tab-card .lines { color: var(--paysol-muted); font-size: 12px; }

.type-pill { text-transform: uppercase; font-size: 10px; letter-spacing: 1px; padding: 2px 8px; border-radius: 4px; font-weight: 600; }
.type-pill.walkin   { background: rgba(42,169,171,0.15); color: var(--paysol-teal); }
.type-pill.table    { background: rgba(255,193,7,0.15);  color: #b07600; }
.type-pill.takeaway { background: rgba(103,58,183,0.15); color: #6a3fbb; }

.opened { color: var(--paysol-muted); font-size: 11px; display: inline-flex; align-items: center; gap: 3px; }
.opened.aging { color: #c62828; font-weight: 600; }

.empty-state { text-align: center; padding: 40px 20px; }
.empty-state p { margin: 12px 0 0; }

.modal-back { position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 200; display: flex; align-items: center; justify-content: center; padding: 20px; }
.modal { background: var(--paysol-card); border: 1px solid var(--paysol-line); border-radius: 10px; padding: 22px; max-width: 400px; width: 100%; }
.modal h3 { margin-top: 0; }
.modal input { width: 100%; margin-top: 6px; }
.modal-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 16px; }
.err { color: var(--paysol-danger); font-size: 12px; text-align: center; margin-top: 10px; }
</style>
